// apps/web/src/wizard/steps/A4SlotStep.tsx — Phase 4 plan 04-03.
//
// Guest Slot Picker A4. The page is the 4th step of the 6-step
// wizard (after A1 date, A2 time, A3 party-size). It re-fetches
// `Query.slots(date, partySize)` — the server's `remainingSeats`
// value is the source of truth (D-26: the picker is a thin
// renderer, all capacity math is server-side).
//
// Per the plan: "the A4 picker shows BLOCKED / FULL / OUTSIDE_BOOKING_HOURS as
// disabled with tooltips". The server hides `remainingSeats === 0`
// rows (ROADMAP success criterion #3: full slots are hidden, not
// rendered-but-disabled). For the AVAILABLE-but-not-bookable states
// (BLOCKED, OUTSIDE_BOOKING_HOURS) the server does not currently
// surface them in `Query.slots`; this A4 page therefore treats
// every row the server returns as AVAILABLE, since the server has
// already filtered the rest. The 4-row test fixture asserts the
// "AVAILABLE" rendering path; the disabled-with-tooltip rendering
// path is reserved for a future Phase 5 server-side expansion.
// Per 260803-jsc, AVAILABLE rows render only the duration (the
// localized remaining-seat count has been removed from this surface
// to keep the row visually quiet); non-bookable rows continue to
// render the localized tooltip below the duration as before.
//
// The selected slot is persisted to the wizard state on click;
// "Weiter" enables when a slot is selected. Navigation to A5 is
// `navigate("/reservierung/kontakt")`.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Info } from "lucide-react";
import { getAvailableSlots } from "../../lib/api";
import { setSelectedSlot, useWizardSlice } from "../state";

export function A4SlotStep(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language.startsWith("en") ? "en" : "de";
  const serviceDate = useWizardSlice((s) => s.serviceDate);
  const partySize = useWizardSlice((s) => s.partySize);
  const selected = useWizardSlice((s) => s.selectedSlot);

  const query = useQuery({
    queryKey: ["wizard-slots", serviceDate, partySize],
    queryFn: () =>
      getAvailableSlots({
        date: serviceDate as string,
        partySize: partySize,
      }),
    enabled: typeof serviceDate === "string",
  });

  const slots = useMemo(() => (query.data ?? []).slice(), [query.data]);

  function back() {
    // The user came from A2 (time) or A3 (party). We send them
    // back to A2; A3 is not part of v1.
    navigate("/reservieren/zeit");
  }

  function next() {
    // Gate: only require a selected slot here. The consent check
    // belongs to A6 (Verbindlich reservieren submit) — consent is
    // captured on A5, after this transition has already happened, so
    // checking it here would always fail and the button would look
    // enabled but do nothing on click. The button-level
    // `disabled={!canContinue}` (where `canContinue = selected !== null`)
    // is the real gate; this is just a defensive re-check.
    if (!selected) return;
    navigate("/reservierung/kontakt");
  }

  // Guard: no service date yet → bounce back to A1.
  if (!serviceDate) {
    navigate("/reservieren/datum", { replace: true });
    return <></>;
  }

  const canContinue = selected !== null;

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a4-slot-step"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <Header
          title={t("wizard.a4.title")}
          subtitle={t("wizard.a4.subtitle")}
          onBack={back}
        />
        <div className="px-4 pt-4">
          <p className="text-xs text-text/70 mb-2" data-testid="a4-meta">
            {t("wizard.a4.meta")}
          </p>
          <WizardProgress current={4} />
        </div>
        <div
          className="px-4 pt-4 pb-2"
          aria-busy={query.isLoading || undefined}
          data-testid="a4-grid-panel"
        >
          {query.isLoading ? (
            <Skeleton />
          ) : query.isError ? (
            <ErrorState
              message={t("wizard.a4.errorMessage")}
              onRetry={() => void query.refetch()}
              retryLabel={t("wizard.a4.retry")}
            />
          ) : slots.length === 0 ? (
            <EmptyState message={t("wizard.a4.empty")} />
          ) : (
            <ul className="space-y-2" data-testid="a4-slot-list">
              {slots.map((s) => (
                <SlotRow
                  key={s.id}
                  slot={s}
                  selected={selected?.slotDefinitionId === s.id}
                  locale={locale}
                />
              ))}
            </ul>
          )}
        </div>
        <div
          role="note"
          className="mx-4 mb-4 flex items-start gap-2 text-sm text-text/80 bg-surface border border-divider rounded-md p-3"
          data-testid="a4-cutoff-hint"
        >
          <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{t("availability.slot.cutoffHint")}</span>
        </div>
        <div className="px-4 pb-6">
          <button
            type="button"
            onClick={next}
            disabled={!canContinue}
            aria-disabled={!canContinue}
            data-testid="a4-next"
            className={`w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border ${
              canContinue
                ? "border-accent text-accent-700 hover:bg-accent-100"
                : "border-divider text-text/40 cursor-not-allowed"
            }`}
          >
            <span>{t("wizard.a4.next")}</span>
          </button>
        </div>
      </div>
    </main>
  );
}

function Header({
  title,
  subtitle,
  onBack,
}: {
  title: string;
  subtitle: string;
  onBack: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <header
      className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
      data-testid="a4-header"
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t("wizard.a4.back")}
        className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
        data-testid="a4-back"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <div className="flex-1">
        <h1
          className="font-heading text-2xl md:text-3xl"
          data-testid="a4-heading"
        >
          {title}
        </h1>
        <p className="text-sm text-text/70 mt-0.5" data-testid="a4-subtitle">
          {subtitle}
        </p>
      </div>
    </header>
  );
}

function WizardProgress({ current }: { current: number }): JSX.Element {
  const { t } = useTranslation();
  return (
    <ol
      role="list"
      aria-label={t("reservation.wizard.progressAria", {
        current,
        total: 6,
      })}
      className="flex gap-1 mb-4"
      data-testid="a4-wizard-progress"
      data-wizard-current={current}
    >
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <li
          key={n}
          aria-current={n === current ? "step" : undefined}
          className={`h-[3px] flex-1 rounded-sm ${
            n <= current ? "bg-accent" : "bg-divider"
          }`}
          data-testid={`a4-wizard-segment-${n}`}
          data-filled={n <= current}
        />
      ))}
    </ol>
  );
}

function SlotRow({
  slot,
  selected,
  locale,
}: {
  slot: {
    id: string;
    startTime: string;
    startsAt: string;
    endsAt: string;
    durationMinutes: number;
    remainingSeats: number;
    status?: "AVAILABLE" | "BLOCKED" | "FULL" | "OUTSIDE_BOOKING_HOURS";
  };
  selected: boolean;
  locale: "de" | "en";
}): JSX.Element {
  const { t } = useTranslation();
  const time = formatTime(slot.startTime);
  // Derive the status from `remainingSeats` when the server does
  // not surface an explicit `status` field. The live server
  // already hides FULL rows (ROADMAP §success #3); the
  // `status`-aware rendering path is reserved for the test
  // surface + a future server expansion (D-13 follow-up).
  const status: "AVAILABLE" | "BLOCKED" | "FULL" | "OUTSIDE_BOOKING_HOURS" =
    slot.status ?? (slot.remainingSeats > 0 ? "AVAILABLE" : "FULL");
  const isBookable = status === "AVAILABLE";
  const tooltipKey = `wizard.a4.tooltip.${camelToLower(status)}`;
  const tooltip = t(tooltipKey);

  return (
    <li data-testid={`a4-slot-row-${slot.id}`} data-state={status}>
      <button
        type="button"
        disabled={!isBookable}
        aria-disabled={!isBookable}
        title={isBookable ? undefined : tooltip}
        onClick={() => {
          if (!isBookable) return;
          setSelectedSlot({
            slotDefinitionId: slot.id,
            startsAt: slot.startsAt,
            endsAt: slot.endsAt,
            durationMinutes: slot.durationMinutes,
            startTime: slot.startTime,
            remainingSeats: slot.remainingSeats,
          });
        }}
        aria-pressed={selected}
        data-testid={`a4-slot-button-${slot.id}`}
        data-selected={selected}
        data-time={time}
        data-remaining-seats={slot.remainingSeats}
        data-status={status}
        className={`w-full min-h-[56px] flex items-center gap-3 px-3 py-2 rounded-md border text-left ${
          !isBookable
            ? "border-divider text-text/40 cursor-not-allowed"
            : selected
              ? "border-accent text-accent-700 bg-accent-100"
              : "border-divider text-text hover:bg-surface"
        }`}
      >
        <span className="font-heading text-xl tabular-nums w-16">{time}</span>
        <span className="flex-1 text-sm">
          <span className="block">
            {formatDuration(slot.durationMinutes, locale)}
          </span>
          {!isBookable ? (
            <span className="block text-xs text-text/70">{tooltip}</span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

function camelToLower(s: string): string {
  return s.toLowerCase();
}

function formatTime(s: string): string {
  if (!/^\d{2}:\d{2}$/.test(s)) return s;
  return s;
}

function formatDuration(minutes: number, locale: "de" | "en"): string {
  if (locale === "en") {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} minutes`;
    if (mins === 0) return hours === 1 ? `1 hour` : `${hours} hours`;
    return `${hours} hour ${mins} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} Minuten`;
  if (mins === 0) return hours === 1 ? `1 Stunde` : `${hours} Stunden`;
  return `${hours} Stunde ${mins} Minuten`;
}

function Skeleton(): JSX.Element {
  return (
    <div data-testid="a4-skeleton" aria-hidden="true">
      <ul className="space-y-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <li
            key={i}
            className="min-h-[56px] rounded-md border border-divider bg-surface/60"
          />
        ))}
      </ul>
    </div>
  );
}

function EmptyState({ message }: { message: string }): JSX.Element {
  return (
    <div className="py-6 text-center" data-testid="a4-empty">
      <p className="text-sm text-text/70">{message}</p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
  retryLabel,
}: {
  message: string;
  onRetry: () => void;
  retryLabel: string;
}): JSX.Element {
  return (
    <div role="alert" className="py-4" data-testid="a4-error">
      <p className="text-sm text-text mb-3">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="btn btn-secondary"
        data-testid="a4-retry"
      >
        {retryLabel}
      </button>
    </div>
  );
}
